import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import PayoutModal from '../../components/payout/PayoutModal';
import './MarketerPortal.css';

// All marketer API calls send the httpOnly auth cookie set by the backend
// at /marketer/signup, /marketer/login, etc. Auth tokens are never read
// or written from JS — see CLAUDE.md security guideline.
const marketerApi = axios.create({
  withCredentials: true
});

const API_BASE = process.env.REACT_APP_API_URL || '';

const getPlatformDomain = () => {
  const env = process.env.REACT_APP_ENV || process.env.NODE_ENV || 'development';
  if (env === 'development') {
    return process.env.REACT_APP_PLATFORM_DOMAIN || 'localhost:3000';
  }
  return process.env.REACT_APP_PLATFORM_DOMAIN || process.env.REACT_APP_PLATFORM_DOMAIN_PRODUCTION || 'userevstar.com';
};

const formatCurrency = (amount) => {
  const value = Number(amount) || 0;
  return new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: 'NGN',
    minimumFractionDigits: 0
  }).format(value);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

// Minimal Icons
const Icon = ({ children, size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    {children}
  </svg>
);

const ArrowRightIcon = () => <Icon><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></Icon>;
const CopyIcon = () => <Icon><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></Icon>;
const CheckIcon = () => <Icon><polyline points="20 6 9 17 4 12"/></Icon>;
const ShareIcon = () => <Icon><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></Icon>;
const LogOutIcon = () => <Icon size={18}><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></Icon>;
const EyeIcon = () => <Icon size={18}><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></Icon>;
const EyeOffIcon = () => <Icon size={18}><path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></Icon>;
const WalletIcon = () => <Icon><path d="M21 12V7H5a2 2 0 010-4h14v4"/><path d="M3 5v14a2 2 0 002 2h16v-5"/><path d="M18 12a2 2 0 000 4h4v-4z"/></Icon>;

export default function MarketerPortal() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authMode, setAuthMode] = useState('signup'); // 'signup', 'login', 'forgot', 'reset'
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState(null);
  const [authSuccess, setAuthSuccess] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [resetToken, setResetToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [copied, setCopied] = useState(false);
  const [rewardConfig, setRewardConfig] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [payoutModalOpen, setPayoutModalOpen] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = (message, type = 'info') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  const fetchConfig = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/referral/config`);
      if (res.data.success) setRewardConfig(res.data.config);
    } catch (err) {
      console.error('Error fetching config:', err);
    }
  }, []);

  // Detect a live cookie session via /marketer/me before showing any UI.
  // The endpoint is cheap, always 200s, and tells us whether to render
  // the dashboard or the auth screen.
  useEffect(() => {
    fetchConfig();

    // Check for reset token in URL
    const urlParams = new URLSearchParams(window.location.search);
    const resetTokenParam = urlParams.get('reset');
    if (resetTokenParam) {
      setResetToken(resetTokenParam);
      setAuthMode('reset');
      window.history.replaceState({}, document.title, window.location.pathname);
      setLoading(false);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const res = await marketerApi.get(`${API_BASE}/api/referral/marketer/me`);
        if (!cancelled && res.data?.authenticated) {
          setIsAuthenticated(true);
        } else if (!cancelled) {
          setLoading(false);
        }
      } catch {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [fetchConfig]);

  const fetchDashboard = useCallback(async () => {
    try {
      setLoading(true);
      const res = await marketerApi.get(`${API_BASE}/api/referral/marketer/dashboard`);
      if (res.data.success) setDashboard(res.data);
    } catch (err) {
      if (err.response?.status === 401) {
        setIsAuthenticated(false);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPayouts = useCallback(async () => {
    try {
      const res = await marketerApi.get(`${API_BASE}/api/referral/marketer/payouts`);
      if (res.data.success) setPayouts(res.data.payouts || []);
    } catch (err) {
      // Non-fatal — payouts list is supplementary
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchDashboard();
      fetchPayouts();
    }
  }, [isAuthenticated, fetchDashboard, fetchPayouts]);

  // Payout API bindings — passed to the modal so it stays purely
  // presentational. Auth rides on the cookie via withCredentials.
  const payoutApi = {
    loadBanks: async () => {
      const res = await marketerApi.get(`${API_BASE}/api/referral/marketer/payouts/banks`);
      return res.data?.banks || [];
    },
    verifyAccount: async ({ accountNumber, bankCode }) => {
      const res = await marketerApi.post(
        `${API_BASE}/api/referral/marketer/payouts/verify-account`,
        { accountNumber, bankCode }
      );
      return res.data;
    },
    submit: async (payload) => {
      const res = await marketerApi.post(
        `${API_BASE}/api/referral/marketer/payouts/request`,
        payload
      );
      return res.data;
    }
  };

  const handleAuth = async (e) => {
    e.preventDefault();
    setAuthError(null);
    setAuthSuccess(null);
    setAuthLoading(true);

    const endpoint = authMode === 'login' ? 'login' : 'signup';
    const payload = authMode === 'login'
      ? { email: email.trim().toLowerCase(), password }
      : { email: email.trim().toLowerCase(), password, name: name.trim(), phone: phone.trim() };

    try {
      // Backend sets the revstar_marketer_token httpOnly cookie on success.
      const res = await marketerApi.post(`${API_BASE}/api/referral/marketer/${endpoint}`, payload);
      if (res.data.success) {
        setIsAuthenticated(true);
        setEmail('');
        setPassword('');
        setName('');
        setPhone('');
      }
    } catch (err) {
      setAuthError(err.response?.data?.error || 'Something went wrong');
    } finally {
      setAuthLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setAuthError(null);
    setAuthSuccess(null);
    setAuthLoading(true);

    try {
      const res = await marketerApi.post(`${API_BASE}/api/referral/marketer/forgot-password`, {
        email: email.trim().toLowerCase()
      });
      if (res.data.success) {
        setAuthSuccess('If an account exists with this email, you will receive a password reset link.');
        setEmail('');
      }
    } catch (err) {
      setAuthError(err.response?.data?.error || 'Something went wrong');
    } finally {
      setAuthLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setAuthError(null);
    setAuthSuccess(null);

    if (password !== confirmPassword) {
      setAuthError('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      setAuthError('Password must be at least 8 characters');
      return;
    }

    setAuthLoading(true);

    try {
      const res = await marketerApi.post(`${API_BASE}/api/referral/marketer/reset-password`, {
        token: resetToken,
        password
      });
      if (res.data.success) {
        setAuthSuccess('Password reset successfully! You can now sign in.');
        setPassword('');
        setConfirmPassword('');
        setResetToken(null);
        setTimeout(() => {
          setAuthMode('login');
          setAuthSuccess(null);
        }, 2000);
      }
    } catch (err) {
      setAuthError(err.response?.data?.error || 'Something went wrong');
    } finally {
      setAuthLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await marketerApi.post(`${API_BASE}/api/referral/marketer/logout`);
    } catch {
      // Even if the request fails, drop the local session — the cookie
      // will eventually expire and a fresh login is harmless.
    }
    setIsAuthenticated(false);
    setDashboard(null);
    setPayouts([]);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const getReferralLink = () => {
    if (!dashboard?.referrer?.referralCode) return '';
    const domain = getPlatformDomain();
    const protocol = domain.includes('localhost') ? 'http' : 'https';
    return `${protocol}://${domain}/signup?ref=${dashboard.referrer.referralCode}`;
  };

  const shareReferral = async () => {
    const link = getReferralLink();
    if (navigator.share) {
      await navigator.share({ title: 'Join Revstar', url: link });
    } else {
      copyToClipboard(link);
    }
  };

  // Get max reward for display. Supports both shapes during transition:
  //   New nested:  { TIER: { base: N, performance: N } }
  //   Legacy flat: { TIER: N }
  const getMaxReward = () => {
    const rewards = rewardConfig?.marketerRewardsByTier || {};
    const values = [];

    for (const entry of Object.values(rewards)) {
      if (typeof entry === 'number') {
        values.push(entry);
      } else if (entry && typeof entry === 'object') {
        if (typeof entry.performance === 'number') values.push(entry.performance);
        if (typeof entry.base === 'number') values.push(entry.base);
      }
    }

    const positives = values.filter(v => v > 0);
    if (positives.length > 0) return Math.max(...positives);

    // Fallback before config loads — matches the published spec performance ceiling
    return 500;
  };

  // Loading
  if (loading && isAuthenticated) {
    return (
      <div className="mkp">
        <div className="mkp-loading">
          <div className="mkp-loading-spinner" />
        </div>
      </div>
    );
  }

  // Auth Screen - Clean & Minimal
  if (!isAuthenticated) {
    // Get auth screen content based on mode
    const getAuthContent = () => {
      if (authMode === 'forgot') {
        return {
          title: 'Reset Password',
          subtitle: 'Enter your email to receive a reset link'
        };
      }
      if (authMode === 'reset') {
        return {
          title: 'Set New Password',
          subtitle: 'Enter your new password below'
        };
      }
      if (authMode === 'login') {
        return {
          title: 'Welcome back',
          subtitle: 'Sign in to your dashboard'
        };
      }
      return {
        title: <>Earn up to <span className="mkp-highlight">{formatCurrency(getMaxReward())}</span> per referral</>,
        subtitle: 'Join thousands of marketers earning with Revstar'
      };
    };

    const authContent = getAuthContent();

    return (
      <div className="mkp mkp-auth">
        <div className="mkp-auth-bg" />

        <div className="mkp-auth-container">
          <div className="mkp-auth-card">
            {/* Brand */}
            <div className="mkp-brand">
              <span className="mkp-brand-logo">R</span>
              <span className="mkp-brand-name">Revstar</span>
            </div>

            {/* Hero Text */}
            <h1 className="mkp-hero">{authContent.title}</h1>
            <p className="mkp-subtext">{authContent.subtitle}</p>

            {/* Success Message */}
            {authSuccess && <div className="mkp-success">{authSuccess}</div>}

            {/* Error */}
            {authError && <div className="mkp-error">{authError}</div>}

            {/* Forgot Password Form */}
            {authMode === 'forgot' && (
              <form onSubmit={handleForgotPassword} className="mkp-form">
                <input
                  type="email"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="mkp-input"
                />

                <button type="submit" className="mkp-submit" disabled={authLoading}>
                  {authLoading ? (
                    <span className="mkp-btn-loading" />
                  ) : (
                    <>
                      Send Reset Link
                      <ArrowRightIcon />
                    </>
                  )}
                </button>
              </form>
            )}

            {/* Reset Password Form */}
            {authMode === 'reset' && (
              <form onSubmit={handleResetPassword} className="mkp-form">
                <div className="mkp-input-wrap">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="New password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={8}
                    className="mkp-input"
                  />
                  <button
                    type="button"
                    className="mkp-eye-btn"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                  </button>
                </div>

                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  minLength={8}
                  className="mkp-input"
                />

                <button type="submit" className="mkp-submit" disabled={authLoading}>
                  {authLoading ? (
                    <span className="mkp-btn-loading" />
                  ) : (
                    <>
                      Reset Password
                      <ArrowRightIcon />
                    </>
                  )}
                </button>
              </form>
            )}

            {/* Login/Signup Form */}
            {(authMode === 'login' || authMode === 'signup') && (
              <form onSubmit={handleAuth} className="mkp-form">
                {authMode === 'signup' && (
                  <input
                    type="text"
                    placeholder="Your name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    className="mkp-input"
                  />
                )}

                <input
                  type="email"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="mkp-input"
                />

                {authMode === 'signup' && (
                  <input
                    type="tel"
                    placeholder="WhatsApp number (e.g. +234 80 1234 5678)"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    required
                    className="mkp-input"
                    autoComplete="tel"
                    inputMode="tel"
                  />
                )}

                <div className="mkp-input-wrap">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={authMode === 'signup' ? 8 : undefined}
                    className="mkp-input"
                  />
                  <button
                    type="button"
                    className="mkp-eye-btn"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                  </button>
                </div>

                {authMode === 'login' && (
                  <button
                    type="button"
                    className="mkp-forgot-link"
                    onClick={() => { setAuthMode('forgot'); setAuthError(null); setAuthSuccess(null); }}
                  >
                    Forgot password?
                  </button>
                )}

                <button type="submit" className="mkp-submit" disabled={authLoading}>
                  {authLoading ? (
                    <span className="mkp-btn-loading" />
                  ) : (
                    <>
                      {authMode === 'signup' ? 'Get Started' : 'Sign In'}
                      <ArrowRightIcon />
                    </>
                  )}
                </button>
              </form>
            )}

            {/* Toggle */}
            <p className="mkp-toggle">
              {authMode === 'signup' && (
                <>Already have an account? <button onClick={() => { setAuthMode('login'); setAuthError(null); setAuthSuccess(null); }}>Sign in</button></>
              )}
              {authMode === 'login' && (
                <>New here? <button onClick={() => { setAuthMode('signup'); setAuthError(null); setAuthSuccess(null); }}>Create account</button></>
              )}
              {(authMode === 'forgot' || authMode === 'reset') && (
                <>Remember your password? <button onClick={() => { setAuthMode('login'); setAuthError(null); setAuthSuccess(null); }}>Sign in</button></>
              )}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Dashboard - Clean & Focused
  const { referrer, referrals = [], recentRewards = [] } = dashboard || {};

  return (
    <div className="mkp mkp-dash">
      {/* Header */}
      <header className="mkp-header">
        <div className="mkp-header-brand">
          <span className="mkp-brand-logo mkp-brand-logo--sm">R</span>
          <span className="mkp-header-title">Referrals</span>
        </div>
        <button className="mkp-logout" onClick={handleLogout}>
          <LogOutIcon />
        </button>
      </header>

      {/* Main Content */}
      <main className="mkp-main">
        {/* User Profile Card */}
        <div className="mkp-profile-card">
          <div className="mkp-profile-avatar">{(referrer?.name || 'M')[0].toUpperCase()}</div>
          <div className="mkp-profile-info">
            <span className="mkp-profile-name">{referrer?.name || 'Marketer'}</span>
            <span className="mkp-profile-email">{referrer?.email}</span>
          </div>
          <span className={`mkp-profile-status mkp-profile-status--${referrer?.status?.toLowerCase()}`}>
            {referrer?.status || 'ACTIVE'}
          </span>
        </div>

        {/* Hero Card - Referral Code */}
        <div className="mkp-code-card">
          <div className="mkp-code-label">Your referral code</div>
          <div className="mkp-code-value">{referrer?.referralCode}</div>
          <div className="mkp-code-actions">
            <button className="mkp-action-btn" onClick={() => copyToClipboard(referrer?.referralCode)}>
              {copied ? <CheckIcon /> : <CopyIcon />}
              <span>{copied ? 'Copied!' : 'Copy'}</span>
            </button>
            <button className="mkp-action-btn mkp-action-btn--primary" onClick={shareReferral}>
              <ShareIcon />
              <span>Share</span>
            </button>
          </div>
        </div>

        {/* Stats Row */}
        <div className="mkp-stats">
          <div className="mkp-stat">
            <span className="mkp-stat-value">{referrer?.totalReferrals || 0}</span>
            <span className="mkp-stat-label">Referrals</span>
          </div>
          <div className="mkp-stat">
            <span className="mkp-stat-value">{referrer?.verifiedReferrals || 0}</span>
            <span className="mkp-stat-label">Verified</span>
          </div>
          <div className="mkp-stat">
            <span className="mkp-stat-value mkp-stat-value--highlight">{formatCurrency(referrer?.totalRewardsEarned || 0)}</span>
            <span className="mkp-stat-label">Earned</span>
          </div>
        </div>

        {/* Earnings */}
        <div className="mkp-earnings">
          <div className="mkp-earning-item">
            <span className="mkp-earning-label">Pending</span>
            <span className="mkp-earning-value">{formatCurrency(referrer?.pendingRewards || 0)}</span>
          </div>
          <div className="mkp-earning-item">
            <span className="mkp-earning-label">Paid</span>
            <span className="mkp-earning-value mkp-earning-value--paid">{formatCurrency(referrer?.paidRewards || 0)}</span>
          </div>
        </div>

        {/* Wallet + Payout */}
        <div className="mkp-wallet">
          <div className="mkp-wallet-info">
            <div className="mkp-wallet-icon" aria-hidden="true"><WalletIcon /></div>
            <div className="mkp-wallet-text">
              <span className="mkp-wallet-label">Available to withdraw</span>
              <span className="mkp-wallet-value">{formatCurrency(referrer?.walletBalance || 0)}</span>
            </div>
          </div>
          <button
            type="button"
            className="mkp-wallet-btn"
            onClick={() => {
              const min = Number(referrer?.minimumPayout) || 2000;
              if ((Number(referrer?.walletBalance) || 0) < min) {
                showToast(`You need at least ${formatCurrency(min)} to request a payout`, 'info');
                return;
              }
              setPayoutModalOpen(true);
            }}
          >
            Request Payout
          </button>
        </div>

        {/* Payout history */}
        {payouts.length > 0 && (
          <div className="payout-history">
            <h3 className="payout-history__title">Payout requests</h3>
            <div className="payout-history__list">
              {payouts.slice(0, 6).map((p) => (
                <div key={p.id} className="payout-history__item">
                  <div className="payout-history__main">
                    <div className="payout-history__amount">{formatCurrency(p.amount)}</div>
                    <div className="payout-history__meta">
                      {p.bankName} · ••{String(p.accountNumber || '').slice(-4)} · {formatDate(p.createdAt)}
                    </div>
                  </div>
                  <span className={`payout-history__badge payout-history__badge--${(p.status || '').toLowerCase()}`}>
                    {p.status}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recent Activity */}
        {(referrals.length > 0 || recentRewards.length > 0) && (
          <div className="mkp-activity">
            <h3 className="mkp-section-title">Recent Activity</h3>
            <div className="mkp-activity-list">
              {referrals.slice(0, 5).map((r) => (
                <div key={r.id} className="mkp-activity-item">
                  <div className="mkp-activity-avatar">{(r.referredName || 'A')[0]}</div>
                  <div className="mkp-activity-info">
                    <span className="mkp-activity-name">{r.referredName || 'Anonymous'}</span>
                    <span className="mkp-activity-meta">{formatDate(r.signedUpAt)} · {r.currentTier || 'FREE'}</span>
                  </div>
                  <span className={`mkp-activity-status mkp-activity-status--${r.status?.toLowerCase()}`}>
                    {r.status}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {referrals.length === 0 && (
          <div className="mkp-empty">
            <p>No referrals yet</p>
            <span>Share your code to start earning</span>
          </div>
        )}
      </main>

      {/* Payout Modal */}
      <PayoutModal
        open={payoutModalOpen}
        onClose={() => setPayoutModalOpen(false)}
        walletBalance={Number(referrer?.walletBalance) || 0}
        minimumPayout={Number(referrer?.minimumPayout) || 2000}
        loadBanks={payoutApi.loadBanks}
        verifyAccount={payoutApi.verifyAccount}
        submit={payoutApi.submit}
        showToast={showToast}
        onSuccess={() => {
          fetchDashboard();
          fetchPayouts();
        }}
      />

      {/* Toast */}
      {toast && (
        <div className={`mkp-toast mkp-toast--${toast.type}`}>{toast.message}</div>
      )}
    </div>
  );
}
